import React, {FC, Fragment} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Loader} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {PreviewPopper} from '../../../../../libs/shared/ui/src/components/popovers/previewPopper';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {
  AnalyticsFilterRelationsEnum,
  AnalyticsFilterTypesEnum,
  AnalyticsInboxScopeFiltersEnum,
  AnalyticsSavedViewTermWithIds,
  isInboxScopeTerm,
  isTermWithoutTags,
} from '../../../../models/api/analyticsSavedViewModel';
import {AnalyticsSavedView} from '../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {filterRenderers} from '../../../helpers/analyticsFiltersHelpers';
import {getFilterTypeFromTerm, isResourceKey} from '../../../helpers/analyticsSavedViewsHelpers';
import {AnalyticsResourceMap} from './reportHeaderSavedViewsDropdown';

/*
 * Props.
 */

interface SavedViewsDropdownItemPreviewProps {
  anchor: Anchor;
  item: AnalyticsSavedView;
  resourceMaps: AnalyticsResourceMap;
}

/*
 * Styles.
 */

const StyledTitle = styled.div`
  height: 40px;
  ${ellipsis()};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  color: ${(p) => p.theme.greys.shade90};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  padding: 12px;
  text-align: center;
`;

const StyledPreviewPopper = styled(PreviewPopper)`
  display: flex;
  flex-direction: column;
`;

const StyledContent = styled.div`
  flex: 1;
  padding: 0 12px;
  overflow-y: auto;
`;

const StyledTermTitle = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  margin: 7px 0;
`;

const StyledResource = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  padding: 7px;
  ${ellipsis()};
`;

const StyledMoreItems = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
  padding: 7px 12px;
`;

const StyledLoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
`;

/*
 * Component.
 */

export const SavedViewsDropdownItemPreview: FC<SavedViewsDropdownItemPreviewProps> = ({
  anchor,
  item,
  resourceMaps,
}) => (
  <StyledPreviewPopper anchor={anchor}>
    <StyledTitle>{item.name}</StyledTitle>
    <StyledContent>{renderFilters(item, resourceMaps)}</StyledContent>
  </StyledPreviewPopper>
);

/*
 * Helpers.
 */

const getTermTitle = ({type, negated, relation}: AnalyticsSavedViewTermWithIds) => {
  switch (type) {
    case AnalyticsFilterTypesEnum.ACCOUNTS:
      return <Trans id="analytics-saved-view-dropdown-preview-accounts">Accounts</Trans>;

    case AnalyticsFilterTypesEnum.INBOXES:
      return <Trans id="analytics-saved-view-dropdown-preview-inboxes">Inboxes</Trans>;

    case AnalyticsFilterTypesEnum.TAGS:
      if (negated) {
        return <Trans id="analytics-saved-view-dropdown-preview-exclude-tag">Exclude tags</Trans>;
      }

      if (relation === AnalyticsFilterRelationsEnum.OR) {
        return <Trans id="analytics-saved-view-dropdown-preview-one-of-selected">Tags are one of</Trans>;
      }

      return <Trans id="analytics-saved-view-dropdown-preview-all-of-selected">Tags are all of</Trans>;

    case AnalyticsFilterTypesEnum.CHANNELS:
      return <Trans id="analytics-saved-view-dropdown-preview-channels">Channels</Trans>;

    case AnalyticsFilterTypesEnum.TEAMMATES:
      return <Trans id="analytics-saved-view-dropdown-preview-teammates">Teammates</Trans>;

    case AnalyticsFilterTypesEnum.TEAMS:
      return <Trans id="analytics-saved-view-dropdown-preview-teams">Teams</Trans>;

    // Default statement, this should not happen.
    default:
      throw new UnreachableError(type as never);
  }
};

const getResourceMap = (resourceMaps: AnalyticsResourceMap, term: AnalyticsSavedViewTermWithIds) => {
  const filterType = getFilterTypeFromTerm(term);
  if (!isResourceKey(filterType)) {
    return {resourceMap: null, filterType};
  }

  if (term.type === AnalyticsFilterTypesEnum.TAGS || term.type === AnalyticsFilterTypesEnum.EXCLUDE_TAGS) {
    return {resourceMap: resourceMaps.full_tags, filterType: AnalyticsFilterTypesEnum.FULL_TAGS};
  }

  return {resourceMap: resourceMaps[filterType], filterType};
};

const renderInboxScopeTerm = (filter: AnalyticsInboxScopeFiltersEnum) => {
  switch (filter) {
    case AnalyticsInboxScopeFiltersEnum.ALL_INBOXES:
      return (
        <StyledTermTitle key="all_inboxes">
          <Trans id="analytics-saved-view-dropdown-preview-all-inboxes">All inboxes</Trans>
        </StyledTermTitle>
      );
    case AnalyticsInboxScopeFiltersEnum.ALL_PRIVATE_INBOXES:
      return (
        <StyledTermTitle key="private_inboxes">
          <Trans id="analytics-saved-view-dropdown-preview-all-private-inboxes">All individual inboxes</Trans>
        </StyledTermTitle>
      );
    case AnalyticsInboxScopeFiltersEnum.ALL_SHARED_INBOXES:
      return (
        <StyledTermTitle key="shared_inboxes">
          <Trans id="analytics-saved-view-dropdown-preview-all-shared-inboxes">All team inboxes</Trans>
        </StyledTermTitle>
      );
    default:
      throw new UnreachableError(filter as never);
  }
};

const renderFilters = (
  {filters, metadata: {hasAllInboxes}}: AnalyticsSavedView,
  resourceMaps: AnalyticsResourceMap,
) =>
  filters.terms.map((term) => {
    if (isTermWithoutTags(term)) {
      return (
        <StyledTermTitle key="without_tags">
          <Trans id="analytics-saved-view-dropdown-preview-without-tags">Without Tags</Trans>
        </StyledTermTitle>
      );
    }

    if (isInboxScopeTerm(term)) {
      return renderInboxScopeTerm(term.special_value);
    }

    const termTitle = getTermTitle(term);
    const {resourceMap, filterType} = getResourceMap(resourceMaps, term);
    const allResources = resourceMap ? term.ids.map((id) => (id ? resourceMap[id] : undefined)) : [];

    if (allResources.length === 0) {
      return null;
    }

    // We don't have data in the store for all resources.
    const definedResources = allResources.filter(isDefined);

    return (
      <Fragment key={`${term.type}_${term.negated}_${term.relation}`}>
        <StyledTermTitle>{termTitle}</StyledTermTitle>
        {definedResources.map((resource) => (
          <StyledResource key={resource.id}>
            {filterRenderers[filterType].renderItem(resource)}
          </StyledResource>
        ))}
        {maybeDisplayMoreItems(allResources.length - definedResources.length, term.type, hasAllInboxes)}
      </Fragment>
    );
  });

const maybeDisplayMoreItems = (
  sizeDifference: number,
  termType: AnalyticsFilterTypesEnum,
  hasAllInboxes: boolean,
) => {
  if (sizeDifference === 0) {
    return null;
  }

  if (termType === AnalyticsFilterTypesEnum.INBOXES && !hasAllInboxes) {
    return displayInboxesLoader();
  }

  return <StyledMoreItems>+ {sizeDifference}</StyledMoreItems>;
};

// Loader while we are loading the rest of the inboxes.
const displayInboxesLoader = () => (
  <StyledLoaderWrapper>
    <Loader size={VisualSizesEnum.SMALL} />
  </StyledLoaderWrapper>
);
